import {
  LANDING_PLAN_LIMIT,
  LandingCatalogProduct,
  SelectedLandingProduct,
} from "./landingPlanTypes";

interface RankedProduct {
  index: number;
  id: string;
  title: string;
  price: number;
  description: unknown;
  amountOfDays: unknown;
  features: unknown;
  createdMs: number;
  rawPopular: boolean;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function creationTimeMs(createdAt: unknown): number {
  if (typeof createdAt !== "string" || createdAt.length === 0) return 0;
  const parsed = Date.parse(createdAt);
  return Number.isFinite(parsed) ? parsed : 0;
}

function rankEligible(value: unknown, index: number): RankedProduct | null {
  if (!isObject(value)) return null;
  if (value.disabled === true) return null;

  const id = value.id;
  const title = value.title;
  const price = value.price;
  if (typeof id !== "string" || id.trim() === "") return null;
  if (typeof title !== "string" || title.trim() === "") return null;
  if (typeof price !== "number" || !Number.isFinite(price) || price <= 0) return null;

  return {
    index,
    id,
    title,
    price,
    description: value.description,
    amountOfDays: value.amountOfDays,
    features: value.features,
    createdMs: creationTimeMs(value.createdAt),
    rawPopular: value.popular === true,
  };
}

function compareRanked(left: RankedProduct, right: RankedProduct): number {
  if (left.rawPopular !== right.rawPopular) {
    return left.rawPopular ? -1 : 1;
  }
  if (left.price !== right.price) {
    return left.price < right.price ? -1 : 1;
  }
  if (left.createdMs !== right.createdMs) {
    return left.createdMs < right.createdMs ? -1 : 1;
  }
  if (left.id < right.id) return -1;
  if (left.id > right.id) return 1;
  if (left.index < right.index) return -1;
  if (left.index > right.index) return 1;
  return 0;
}

function toSelected(row: RankedProduct, popular: boolean): SelectedLandingProduct {
  return {
    id: row.id,
    title: row.title,
    description: row.description,
    price: row.price,
    amountOfDays: row.amountOfDays,
    features: row.features,
    popular,
  };
}

function applyPopularTreatment(rows: readonly RankedProduct[]): SelectedLandingProduct[] {
  const featuredIndex = rows.findIndex((row) => row.rawPopular);
  return rows.map((row, index) => toSelected(row, index === featuredIndex));
}

/**
 * Choose the plans the landing shows from a raw catalog payload.
 *
 * A product is eligible when it is an object, `disabled` is not strictly
 * true, `id` and `title` are strings whose trim is non-empty, and `price`
 * is a finite number greater than 0. Eligible products are ordered popular
 * first, then by price ascending, creation time ascending, and id ascending.
 * The result keeps the first three (`LANDING_PLAN_LIMIT`) and is not padded
 * when fewer products qualify. A null or undefined catalog returns [].
 *
 * `popular` on each returned row is the single Mais popular treatment. It
 * is true only for the first returned row whose raw popular flag is true,
 * and false for every other row.
 */
export function selectLandingProducts(
  products: readonly LandingCatalogProduct[] | null | undefined,
): SelectedLandingProduct[] {
  if (!Array.isArray(products)) return [];

  const ranked: RankedProduct[] = [];
  for (let index = 0; index < products.length; index += 1) {
    const row = rankEligible(products[index], index);
    if (row !== null) ranked.push(row);
  }

  const ordered = ranked.slice().sort(compareRanked).slice(0, LANDING_PLAN_LIMIT);
  return applyPopularTreatment(ordered);
}
